import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import { withTranslation, Trans } from 'react-i18next'
import { Helmet } from 'react-helmet'
import {
  useErrorService,
  surveyType,
  PrintableArea,
  buildAggregatedAssessmentUsersFileBase,
} from '@abroad/components'
import {
  WisdomAssessmentResult,
  ProsperityAssessmentResult,
  AwarenessAssessmentResult,
} from '../components'
import API from '../utils/API'

const UserCombinedSurveyReport = () => {
  const { state, search } = useLocation()
  const history = useHistory()
  const Error = useErrorService()
  const [survey, setSurvey] = useState(null)
  const [isLoading, setIsLoading] = useState(true)

  const reportContext = useMemo(() => {
    const query = new URLSearchParams(search)
    const surveyIdFromQuery = query.getAll('surveyId').filter(Boolean)
    const surveyId =
      state?.surveyId?.length > 0 ? state.surveyId : surveyIdFromQuery
    const isNetwork =
      Boolean(state?.isNetwork) || query.get('isNetwork') === 'true'

    return {
      surveyId,
      isNetwork,
      companyName: state?.companyName || query.get('organization') || '',
      ecoSystemName: state?.ecoSystemName || query.get('network') || '',
    }
  }, [search, state])

  useEffect(() => {
    if (!reportContext.surveyId?.length) {
      history.replace('/admin/home')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportContext.surveyId, history])

  useEffect(() => {
    const getCombinedResult = async () => {
      try {
        const { data } = await API.AdminAPI.companyList.combinedResult({
          surveyIds: reportContext.surveyId,
        })
        setSurvey(data)
        setIsLoading(false)
      } catch (e) {
        if (
          e?.code === 'ValidationError' ||
          e?.code === 'resource_not_found' ||
          e?.code === 'not_found'
        ) {
          //history.replace('/404')
        } else {
          setIsLoading(false)
          Error.showError(e)
        }
      }
    }
    if (reportContext.surveyId?.length) {
      getCombinedResult()
    } else {
      history.replace('/admin/home')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const entityLabel = reportContext.isNetwork ? 'Network' : 'Organization'
  const entityName = reportContext.isNetwork
    ? reportContext.ecoSystemName
    : reportContext.companyName
  const pdfFileNameBase = buildAggregatedAssessmentUsersFileBase(
    entityName || entityLabel,
  )

  return (
    <>
      <Helmet>
        <title>{'User Combined Survey Report | Abroad'}</title>
        <meta
          name='title'
          content={'User Combined Survey Report | Abroad'}></meta>
        <meta
          property='og:title'
          content={'User Combined Survey Report | Abroad'}></meta>
        <meta
          property='twitter:title'
          content={'User Combined Survey Report | Abroad'}></meta>
        <link
          rel='canonical'
          href={`${process.env.REACT_APP_DOMAIN}/admin/users_combined_report`}></link>
        <meta
          property='og:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/users_combined_report`}></meta>
        <meta
          property='twitter:url'
          content={`${process.env.REACT_APP_DOMAIN}/admin/users_combined_report`}></meta>
      </Helmet>
      <main>
        {survey?.surveyType === surveyType?.WISDOM ? (
          <WisdomAssessmentResult
            survey={survey}
            isLoading={isLoading}
            headerText={`${entityLabel} Wisdom Assessment`}
            subHeaderText='COMBINED SUMMARY RESULTS'
            subHeaderDescription='Below are the aggregate results for how your group rated your organization and themselves during the specified time period.'
            pdfFileNameBase={pdfFileNameBase}
          />
        ) : survey?.surveyType === surveyType?.PROSPERITY ? (
          <ProsperityAssessmentResult
            survey={survey}
            isLoading={isLoading}
            headerText={`${entityLabel} True Prosperity Assessment`}
            subHeaderText='COMBINED SUMMARY RESULTS'
            subHeaderDescription='Below are the aggregate results for how your group rated your organization and themselves during the specified time period.'
            pdfFileNameBase={pdfFileNameBase}
          />
        ) : (
          <AwarenessAssessmentResult
            survey={survey}
            isLoading={isLoading}
            headerText={<Trans>{entityLabel} Awareness Assessment</Trans>}
            pdfTitle={`${entityLabel} Awareness Assessment`}
            pdfSubtitle='COMBINED SUMMARY RESULTS'
            pdfFileNameBase={pdfFileNameBase}
            subHeaderText={<Trans>COMBINED SUMMARY RESULTS</Trans>}
            subHeaderDescription={
              <Trans>
                Below are the aggregate results for how your group rated your
                organization and themselves during the specified time period.
              </Trans>
            }
          />
        )}
      </main>
      <PrintableArea printSelector='main' />
    </>
  )
}

export default withTranslation('translation')(UserCombinedSurveyReport)
